import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommunicationCenterPanel = ({ communications, loading }) => {
  const [selectedChannel, setSelectedChannel] = useState('all');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'الآن';
    if (diffMins < 60) return `منذ ${diffMins} دقيقة`;
    if (diffHours < 24) return `منذ ${diffHours} ساعة`;
    if (diffDays < 7) return `منذ ${diffDays} يوم`;
    
    return new Intl.DateTimeFormat('ar-EG', {
      month: 'short',
      day: 'numeric'
    })?.format(date);
  };

  const getChannelIcon = (channel) => {
    const iconMap = {
      'email': 'Mail',
      'whatsapp': 'MessageCircle',
      'sms': 'MessageSquare',
      'phone': 'Phone',
      'system': 'Bell'
    };
    return iconMap?.[channel?.toLowerCase()] || 'MessageSquare';
  };

  const getChannelColor = (channel) => {
    const colorMap = {
      'email': 'text-blue-500',
      'whatsapp': 'text-green-500',
      'sms': 'text-purple-500',
      'phone': 'text-amber-500',
      'system': 'text-primary'
    };
    return colorMap?.[channel?.toLowerCase()] || 'text-muted-foreground';
  };

  const channels = [
    { value: 'all', label: 'الكل', icon: 'MessageSquare' },
    { value: 'email', label: 'البريد', icon: 'Mail' },
    { value: 'whatsapp', label: 'واتساب', icon: 'MessageCircle' },
    { value: 'system', label: 'النظام', icon: 'Bell' }
  ];

  const filteredCommunications = selectedChannel === 'all' 
    ? communications 
    : communications?.filter(comm => comm?.channel?.toLowerCase() === selectedChannel);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/3"></div>
          <div className="h-16 bg-muted rounded"></div>
          <div className="h-16 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden" dir="rtl">
      {/* Header */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon name="MessageSquare" size={20} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-foreground">مركز التواصل</h3>
          </div>
          <Button variant="outline" size="sm">
            <Icon name="Plus" size={16} />
            <span>رسالة جديدة</span>
          </Button>
        </div>

        {/* Channel Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {channels?.map(channel => (
            <button
              key={channel?.value}
              onClick={() => setSelectedChannel(channel?.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                selectedChannel === channel?.value
                  ? 'bg-primary text-white' :'bg-muted/50 text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon name={channel?.icon} size={16} />
              {channel?.label}
            </button>
          ))}
        </div>
      </div>

      {/* Messages List */}
      <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
        {!filteredCommunications || filteredCommunications?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Inbox" size={40} color="var(--color-muted-foreground)" />
            <p className="text-muted-foreground mt-3">لا توجد رسائل</p>
          </div>
        ) : (
          filteredCommunications?.map((comm, index) => (
            <div 
              key={index}
              className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon 
                  name={getChannelIcon(comm?.channel)} 
                  size={18} 
                  color={`var(--color-${getChannelColor(comm?.channel)?.replace('text-', '')})`} 
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-semibold text-foreground text-sm">
                    {comm?.consultantName}
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(comm?.sentAt)}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {comm?.message}
                </p>
                
                <div className="flex items-center gap-2 mt-2">
                  <span className={`inline-flex items-center gap-1 text-xs ${getChannelColor(comm?.channel)}`}>
                    <Icon name={getChannelIcon(comm?.channel)} size={12} />
                    {comm?.channel}
                  </span>
                  {comm?.status === 'sent' && (
                    <span className="inline-flex items-center gap-1 text-xs text-success">
                      <Icon name="CheckCheck" size={12} />
                      تم الإرسال
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CommunicationCenterPanel;